import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getUserCourses } from "@/lib/user-courses";
import { BookOpen, Play, Loader2 } from "lucide-react";
import { UserCourse } from "@/types/supabase";
import { useIsMobile } from "@/hooks/use-mobile";

type MyCoursesListProps = {
  userId: string | undefined;
};

const MyCoursesList = ({ userId }: MyCoursesListProps) => {
  const [courses, setCourses] = useState<UserCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchCourses = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userCourses = await getUserCourses(userId);
        setCourses(userCourses);
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [userId]);

  const handleStartLearning = (courseId: string) => {
    navigate(`/learner/course/${courseId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-kaija-accent" />
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="text-center py-12">
        <BookOpen className="h-16 w-16 mx-auto text-gray-400 mb-4" />
        <h2 className="text-2xl font-medium mb-3">Sul pole veel ühtegi kursust</h2>
        <p className="text-gray-700 mb-6 max-w-lg mx-auto">
          Alusta oma õpiteekonda meie mitmekesistest kursustest valides
        </p>
        <Button asChild className="bg-kaija-accent hover:bg-opacity-90 text-white">
          <Link to="/courses">Vaata kursuseid</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Minu kursused ({courses.length})</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
        {courses.map((enrollment) => (
          <Card 
            key={enrollment.id} 
            className="overflow-hidden flex flex-col h-full border border-kaija-sage bg-white hover:shadow-md transition-all w-full max-w-[350px]"
          >
            <div className="relative h-40 overflow-hidden">
              <img 
                src={enrollment.course?.image_url} 
                alt={enrollment.course?.title} 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                <Button 
                  className="bg-white text-kaija-accent hover:bg-gray-100"
                  onClick={() => handleStartLearning(enrollment.course_id)}
                >
                  <Play className="mr-2 h-4 w-4" /> Jätka õppimist
                </Button>
              </div>
            </div>
            
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-base text-kaija-accent min-h-[5rem] line-clamp-4">
                {enrollment.course?.title}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="pb-6 pt-2">
              <Button 
                className="w-full mt-2 bg-kaija-accent hover:bg-opacity-90 text-white"
                onClick={() => handleStartLearning(enrollment.course_id)}
              >
                Jätka kursust
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="text-center pt-4">
        <Button asChild variant="outline">
          <Link to="/courses">
            <BookOpen className="mr-2 h-4 w-4" />
            Avasta rohkem kursuseid
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default MyCoursesList;
